import { useState } from "react";
import { FaTimes } from "react-icons/fa";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";

type OrderNfeModalProps = {
  isOpen: boolean;
  orderNumber?: number | string | null;
  danfeUrl?: string | null;
  xmlUrl?: string | null;
  showXmlDownload?: boolean;
  cancellationPdfUrl?: string | null;
  cancellationXmlUrl?: string | null;
  isCancelled?: boolean;
  canCancel?: boolean;
  isCancelling?: boolean;
  onCancel?: (justification: string) => Promise<void> | void;
  onClose: () => void;
};

const OrderNfeModal = ({
  isOpen,
  orderNumber,
  danfeUrl,
  xmlUrl,
  showXmlDownload = true,
  cancellationPdfUrl,
  cancellationXmlUrl,
  isCancelled,
  canCancel,
  isCancelling,
  onCancel,
  onClose
}: OrderNfeModalProps) => {
  const [justification, setJustification] = useState("");

  if (!isOpen) {
    return null;
  }

  return (
    <Modal overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 px-4">
      <div className="bg-white rounded-md shadow-xl px-5 md:px-6 py-7 md:py-8 relative w-full max-w-sm">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-primary-600 hover:text-primary-700 text-2xl font-bold"
          aria-label={"Fechar"}
        >
          <FaTimes />
        </button>
        <div className="mb-4">
          <h4 className="text-lg font-bold text-primary-600">{"Nota fiscal"}</h4>
          {orderNumber != null && (
            <p className="text-xs font-semibold text-gray-600">{"Pedido"} #{orderNumber}</p>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{"Acessar"}</p>
          <Button
            className="w-full py-2"
            onClick={() => danfeUrl && window.open(danfeUrl, "_blank")}
            disabled={!danfeUrl}
          >
            {"VER DANFE (PDF)"}
          </Button>
          {showXmlDownload && (
            <Button
              className="w-full py-2"
              onClick={() => xmlUrl && window.open(xmlUrl, "_blank")}
              disabled={!xmlUrl}
            >
              {"BAIXAR XML"}
            </Button>
          )}
          {canCancel && (
            <div className="mt-2 border-t pt-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{"Cancelar"}</p>
              <label className="text-xs font-semibold text-gray-600">{"Justificativa (opcional)"}</label>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-600"
                rows={3}
                placeholder={"Ex.: Cancelamento solicitado pelo cliente."}
                value={justification}
                onChange={event => setJustification(event.target.value)}
              />
              <Button isLoading={Boolean(isCancelling)} variant="secondary"
                className="w-full py-2"
                onClick={() => onCancel?.(justification)}
                disabled={Boolean(isCancelling)}
              >
                {"CANCELAR NOTA FISCAL"}
              </Button>
            </div>
          )}
          {(cancellationPdfUrl || cancellationXmlUrl) && (
            <div className="mt-2 border-t pt-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{"Cancelamento"}</p>
              <Button variant="muted"
                className="w-full py-2"
                onClick={() => cancellationPdfUrl && window.open(cancellationPdfUrl, "_blank")}
                disabled={!cancellationPdfUrl}
              >
                {"BAIXAR PDF DO CANCELAMENTO"}
              </Button>
              <Button variant="muted"
                className="w-full py-2"
                onClick={() => cancellationXmlUrl && window.open(cancellationXmlUrl, "_blank")}
                disabled={!cancellationXmlUrl}
              >
                {"BAIXAR XML DO CANCELAMENTO"}
              </Button>
            </div>
          )}
          {isCancelled && (
            <p className="text-xs text-gray-500">
              {"NF-e cancelada. Para emitir outra, crie um novo pedido."}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default OrderNfeModal;
